import { Button, Checkbox, Form, Input } from "@telluscompany/design-system";
import { useForm } from "react-hook-form";
import { Filter } from "lucide-react";

import { useLocalization } from "@i18n";
import { cleanEmptyStrings } from "@shared/hooks";

import type { AdminLicenseInventoryFilterData } from "../../schemas";

interface AdminLicenseInventoryFilterProps {
  onFilter: (data: Partial<AdminLicenseInventoryFilterData>) => void;
  onClear: () => void;
  initialFilters?: Partial<AdminLicenseInventoryFilterData>;
}

const EMPTY_FORM: AdminLicenseInventoryFilterData = {
  complex: "",
  structure: "",
  module: "",
  onlyActive: false,
};

export const AdminLicenseInventoryFilter = ({
  onFilter,
  onClear,
  initialFilters,
}: AdminLicenseInventoryFilterProps) => {
  const { translate } = useLocalization();

  const form = useForm<AdminLicenseInventoryFilterData>({
    defaultValues: {
      complex: initialFilters?.complex ?? "",
      structure: initialFilters?.structure ?? "",
      module: initialFilters?.module ?? "",
      onlyActive: initialFilters?.onlyActive ?? false,
    },
  });

  const handleFilter = (data: AdminLicenseInventoryFilterData) => {
    onFilter(
      cleanEmptyStrings(data) as Partial<AdminLicenseInventoryFilterData>,
    );
  };

  const handleClear = () => {
    form.reset(EMPTY_FORM);
    onClear();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFilter)}>
        <div className="flex flex-column gap-5">
          <Input
            control={form.control}
            label={`${translate("admin.licenseInventory.filter.label.complex")}:`}
            name="complex"
            type="text"
            placeholder={translate(
              "admin.licenseInventory.filter.placeholder.complex",
            )}
          />
          <Input
            control={form.control}
            label={`${translate("admin.licenseInventory.filter.label.structure")}:`}
            name="structure"
            type="text"
            placeholder={translate(
              "admin.licenseInventory.filter.placeholder.structure",
            )}
          />
          <Input
            control={form.control}
            label={`${translate("admin.licenseInventory.filter.label.module")}:`}
            name="module"
            type="text"
            placeholder={translate(
              "admin.licenseInventory.filter.placeholder.module",
            )}
          />
          <Checkbox
            control={form.control}
            name="onlyActive"
            label={translate("admin.licenseInventory.filter.label.onlyActive")}
          />
        </div>
        <div className="flex justify-end mb-4 gap-4">
          <Button type="button" variant="secondary" onClick={handleClear}>
            {translate("admin.licenseInventory.filter.button.clearFilters")}
          </Button>
          <Button type="submit" iconLeft={<Filter fill="currentColor" />}>
            {translate("admin.licenseInventory.filter.button.filter")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
